// Fostr Wishlist
// Version: 1.0.0
// Author: Fostr
// Author URI: https://fostr.io
// Description: Wishlist functionality for Shopify Stores

// FUNCTIONALITY NOTES
// --------------------
// Removes duplicate products from the wishlist
// Adds a product to the wishlist, including fetching additional product information from the API, and updating the local storage
// Removes a product from the wishlist, including updating the local storage
// Renders product cards on the wishlist page by creating elements and appending them to the DOM
// On page load sets the state of the wishlist button based on whether the product already exists in the wishlist
// Handles click events on the wishlist button, including adding or removing products from the wishlist and updating the button state
// Fetches product information from the API using GraphQL
// Generates products from customer tags
// Transfers products from the local storage to the customer's wishlist
// Removes a product card from the wishlist page
// Show a notification on the account and wishlist pages when transferring items
// Makes an API call to manage customer tags
// Provides source of where the product was added to the wishlist (PDP, PLP, etc.)

class FostrWishlist extends HTMLElement {

  localStorageItemName = 'fostrWishlist'
  localStorageItem = null
  apiUrl = null

  constructor() {
    super()

    this.apiUrl = fostrWishlist.netlifyUrl1
    if (window.location.hostname.includes('staging')) {
      this.apiUrl = fostrWishlist.netlifyUrlStaging
    } else if (window.location.hostname.includes('??')) {
      this.apiUrl = fostrWishlist.netlifyUrl2
    }

    if (!document.documentElement.dataset.fostrWishlistInitializing) {
      document.documentElement.dataset.fostrWishlistInitializing = 'true'

      this.getLocalStorageItem()
      this.handleLogout()
      this.cleanWishlist()
      this.transferAndMigrateLegacyWishlist()
      this.updateWishlistCountIndicators()
    }
  }

  getFormattedDateTime = dateTime => {
    const year = dateTime.getFullYear()
    const month = dateTime.getMonth() + 1
    const day = dateTime.getDate()
    // Time is not used but is included if needed to add back
    // const hours = dateTime.getHours()
    // const minutes = dateTime.getMinutes()
    // const seconds = dateTime.getSeconds()

    // Format the date,  padding with leading zeroes where needed
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    // Time is not used but is included if needed to add back
    // const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

    // Output: YYYY-MM-DD HH:MM:SS (Time has been removed)
    return `${date}`
  }

  handleLogout() {
    // listen to a click event on href=/account/logout and remove the wishlist from local storage
    document.addEventListener('click', (e) => {
      if (e.target.closest('a[href="/account/logout"]')) {
        localStorage.removeItem(this.localStorageItemName)
      }
    })
  }

  transferAndMigrateLegacyWishlist() {
    // Check if wishlist transfer or migration is already in progress
    if (document.documentElement.dataset.fostrWishlistTransfer === 'true' || document.documentElement.dataset.wishlistMigratingLegacyItems === 'true') {
      return
    }

    if (!window.customer?.id) return

    // Get the legacy wishlist from local storage
    const localStorageItem = this.getLocalStorageItem()


    this.generateProductsFromCustomerTags(window.customer.tags)

    // Get all items from localStorage.products where product.id is not in customer.tags
    let productsToTransfer = localStorageItem.products.filter(item => !window.customer.tags.includes(`wishlist:${item.id}`))

    // Make new variable with products in productsToTransfer that have transferred set to true
    const productsToTransferTrue = productsToTransfer.filter(item => item.transferred === true)

    // Remove productsToTransferTrue from localStorageItem.products
    productsToTransferTrue.forEach(product => {
      localStorageItem.products.splice(localStorageItem.products.indexOf(product), 1)

      // remove from productsToTransfer
      productsToTransfer.splice(productsToTransfer.indexOf(product), 1)

      localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))
    })

    if (productsToTransfer.length <= 0) return
    document.documentElement.dataset.fostrWishlistTransfer = 'true'

    this.showNotification()

    // Use Promise.all() to wait for all promises to resolve
    Promise.all(productsToTransfer.map(product => {
      return new Promise(async (resolve, reject) => {
        try {
          let obj = {
            'customer': window.customer.id.toString(),
            'action': 'add',
            'tag': `wishlist:${product.id}`
          }
          const response = await this.makeApiCall(this.apiUrl, obj)
          resolve(response)
        } catch (err) {
          console.log(err)
          reject(err)
        }
      })
    }))
    .then(() => {
      // transfer complete
      document.documentElement.dataset.fostrWishlistTransfer = 'false'
    })
    .catch(error => {
      // transfer failed
      document.documentElement.dataset.fostrWishlistTransfer = 'false'
      console.error(error)
    })
  }

  showNotification() {

    if (!window.fostrWishlist.transferBannerEnabled) return
    if (!window.fostrWishlist.notificationText) return

    // only show on account page and wishlist page
    if (!document.body.classList.contains('customers-account') && !document.body.classList.contains('page-wishlist')) return

    let notification = document.getElementById('fostr-wishlist-transfer')

    // check if the element already exists on the page
    if (!notification) {
      notification = document.createElement('fostr-announcement')
      notification.id = 'fostr-wishlist-transfer'
      notification.dataset.autoHideDelay = 6000
      notification.innerHTML = window.fostrWishlist.notificationText
      document.body.appendChild(notification)
    }

    // show the notification
    notification.show()
  }

  cleanWishlist() {
    // remove duplicate products from localStorage.products
    let localStorageItem = this.getLocalStorageItem()
    let products = localStorageItem.products

    if (!products) return

    // Use Set object to remove duplicates
    let uniqueProducts = Array.from(new Set(products.map(item => item.id))).map(id => products.find(item => item.id === id))

    localStorageItem.products = uniqueProducts
    localStorageItem.productCount = uniqueProducts.length

    localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))
  }

  productExists = (id) => {
    // check if the product is already in the wishlist return boolean
    const localStorageItem = this.getLocalStorageItem()
    if (!localStorageItem.products) return false

    return localStorageItem.products.find((item) => {
      return item.id === id
    }) || false
  }

  getLocalStorageItem() {
    // check if the localStorage item exists if not create it
    let localStorageItem = JSON.parse(localStorage.getItem(this.localStorageItemName)) || null

    if (localStorageItem === null) {

      // first time user
      let obj = {
        'products': [],
        'customerId': window.customer?.id || null,
        'productCount': 0
      }

      localStorage.setItem(this.localStorageItemName, JSON.stringify(obj))

      if (window.customer) {
        this.generateProductsFromCustomerTags(window.customer.tags).then(() => {
          return obj
        })
      }
    }

    if (window.customer && window.customer.tags) {
      let products = JSON.parse(localStorage.getItem(this.localStorageItemName)).products

      localStorageItem = JSON.parse(localStorage.getItem(this.localStorageItemName))
      localStorageItem.products = products
      localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))
    }


    // check if the customerId has changed
    localStorageItem = JSON.parse(localStorage.getItem(this.localStorageItemName))
    localStorageItem.customerId = window.customer?.id || null
    localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))

    return localStorageItem
  }

  async generateProductsFromCustomerTags(tags, ids) {
    // checks to see if there are customer tags and updates the wishlist localStorage
    // this is when a logged in customer returns on a browser without the old localStorage item
    tags = JSON.parse(tags) || ids

    // check if the customer is logged in
    if (window.customer?.id || ids) {

      // check if the customer has a wishlist tags in window.customer.tags
      if (window.customer && window.customer.tags || ids) {

        // get all tags that contains wishlist
        let wishlistTags = ids || tags.filter(tag => tag.includes("wishlist:"))

        // split wishlist tags by
        if (!ids) {
          wishlistTags = wishlistTags.map(tag => tag.split(":")[1])
        } else {
          wishlistTags = wishlistTags.map(tag => tag)
        }

        let localStorageItem = this.getLocalStorageItem()

        // remove tags in wishlistTags if they are in the localStorageItem.products
        if (localStorageItem.products) {

          wishlistTags = wishlistTags.filter(tag => {
            return !localStorageItem.products.some(item => item.id === tag)
          })
        }

        if (wishlistTags.length > 0) {

          // loop through all wishlist tags
          for (let i = 0; i < wishlistTags.length; i++) {
            // get the product id
            let productId = wishlistTags[i]

            // check if the product exists in the localStorage
            if (this.productExists(productId)) return

            let product = new FostrWishlistProduct()

            await product.getProductInformation(productId).then((data) => {

              if (!data || data.length === 0) return

              product.id = productId
              product.gid = data.id
              product.image_url = data.images.edges[0].node.originalSrc
              product.title = data.title
              product.url = product.generateProductURL(data.handle)
              product.handle = data.handle
              product.price = `${currencySymbol}${data.priceRange.minVariantPrice.amount}`
              product.added_to_wishlist = null
              product.source = null
              product.transferred = window.customer?.tags.includes(`wishlist:${product.id}`) || false

              localStorageItem = this.getLocalStorageItem()
              localStorageItem.products.push(product)
              localStorageItem.productCount = localStorageItem.products.length
              localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))

              return true
            })
          }
        }
      }
    }
  }

  updateLocalStorageItem = (action, product) => {
    if (!product || !action) return
    let localStorageItem = this.getLocalStorageItem()
    let productHandle = product.handle

    if (action === 'add') {
      // check if the product is already in the wishlist
      if (this.productExists(product.id)) return

      localStorageItem.products.push(product)
      localStorageItem.productCount = localStorageItem.products.length
    } else if (action === 'remove') {
      const productToRemove = localStorageItem.products.find((item) => {
        return item.id === product.id
      })
      productHandle = productToRemove.handle

      // remove product product.id from the wishlist
      localStorageItem.products = localStorageItem.products.filter((item) => {
        return item.id !== product.id
      })

      localStorageItem.productCount = localStorageItem.products.length
    }

    localStorage.setItem(this.localStorageItemName, JSON.stringify(localStorageItem))

    // update wishlist page
    this.showWishlistUpdatedModal(action, productHandle)
    this.wishlistPageSetState()
    this.updateWishlistCountIndicators()
  }

  wishlistPageSetState() {
    // checks to see if the wishlist has any items
    // if it does then it will show the wishlist items
    // if it doesn't then it will show the empty wishlist message
    let localStorageItem = this.getLocalStorageItem()

    let wishlistItemsEl = document.querySelector('fostr-wishlist-items')
    if (!wishlistItemsEl) return

    if (localStorageItem.productCount > 0) {
      wishlistItemsEl.classList.remove('wishlist-items--empty')
      wishlistItemsEl.classList.add('wishlist-items--hasItems')
      let uaProductRecommendations = document.querySelector('.ua_product_recommendations')
      if (uaProductRecommendations) {
        uaProductRecommendations.style.display = 'block';
      }
    } else {
      wishlistItemsEl.classList.remove('wishlist-items--hasItems')
      wishlistItemsEl.classList.add('wishlist-items--empty')
    }
  }

  updateWishlistCountIndicators() {
    // update all elements that have the data-wishlist-count attribute
    const wishlistCountEls = document.querySelectorAll('[data-wishlist-count]')
    const wishlistCountTextEls = document.querySelectorAll('[data-wishlist-count-text]')

    if (!wishlistCountEls && !wishlistCountTextEls) return

    const localStorageItem = this.getLocalStorageItem()

    wishlistCountEls.forEach(el => {
      el.dataset.wishlistCount = localStorageItem.productCount
    })

    wishlistCountTextEls.forEach(el => {
      el.innerText = localStorageItem.productCount
    })
  }

  // Handle API requests
  async makeApiCall(url, obj) {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(obj)
      })
      return await response.json()
    } catch (err) {
      console.error(err)
      return err
    }
  }
}
class FostrWishlistProduct {
  id = null
  gid = null
  added_to_wishlist = null // date added to wishlist
  image_url = null
  title = null
  url = null
  handle
  price = null
  source = null // source of where the product was added to the wishlist

  constructor() {
  }

  generateGID = (id) => {
    this.gid = `gid://shopify/Product/${id}`
  }

  setProductSource() {
    this.source = window.getPageSource()
  }

  generateProductURL = (handle) => {
    return `${window.location.origin}/products/${handle}`
  }

  // fetch product information from the API
  async getProductInformation(productId) {

    // get product information from the API using graphql
    const payload = `{
      product(id: "gid://shopify/Product/${productId}") {
        id
        handle
        title
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 1) {
          edges {
            node {
              originalSrc
            }
          }
        }
      }
    }`

    let resp = await window.fostrFetch(payload)

    let data = await resp.json()

    return data.data.product || []
  }
}
class FostrWishlistButton extends FostrWishlist {

  constructor() {
    super()
    // create a new FostrWishlistProduct
    this.product = new FostrWishlistProduct()
    this.product.id = this.getAttribute('data-product-id')
    this.product.generateGID(this.product.id)
    this.product.added_to_wishlist = null
    this.product.image_url = null
    this.product.title = this.getAttribute('data-product-title')
    this.product.url = null
    this.product.handle = null
    this.product.price = this?.dataset?.productPrice || null
    this.product.vendor = this?.dataset?.productVendor || null
    this.product.type = this?.dataset?.productType || null
    this.product.variantId = this?.dataset?.productVariantId || null
    this.product.currency = this?.dataset?.currency || null
    this.product.compareAtPrice = this?.dataset?.compareAtPrice || null
    this.product.setProductSource()
    this.product.transferred = false // flag to check if the product has been transferred to customer's wishlist
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick)
    this.addEventListener('keypress', function(event) {
      if (event.key === 'Enter') {
        this.onClick();
      }
    }.bind(this));
    this.updateButtonState()
  }

  getAnalyticsData() {

    let data = {
      product: {
        id: this.product.id,
        title: this.product.title,
        price: this.product.price,
        vendor: this.product.vendor,
        type: this.product.type,
        variantId: this.product.variantId,
        currency: this.product.currency
      },

      customer: {
        id: window.customer?.id || null,
        isLoggedIn: window.customer ? true : false
      },

      action: this.action
    }

    return data
  }

  removeProductCardFromDOM() {
    // remove the closest product card from the wishlist page
    let productCard = this.closest('fostr-wishlist-items')?.querySelector('[data-product-id="' + this.product.id + '"]')
    if (productCard) {
      productCard.parentNode.remove()
    }
  }

  updateButtonState() {
    // sets the data-action attribute of the button on page load
    // so it is full if the item is already in the wishlist, empty if not
    if (this.productExists(this.product.id)) {
      this.dataset.action = 'remove'
    }
  }

  // handle the click event
  onClick() {
    if (this.productExists(this.product.id)) {
      this.removeFromWishlist()
    } else if (!this.productExists(this.product.id)) {
      this.addToWishlist()
    }
  }

  async showWishlistUpdatedModal (action, productHandle) {
    const modalWrapper = document.querySelector('.js-wishlist-updated-modal-wrapper')

    if (!modalWrapper || !productHandle) return

    const url = `/products/${productHandle}`
    const section = await window.BAO.sectionRenderer.fetchSingle(
      'wishlist-updated-modal',
      url,
    )
    const html = window.BAO.sectionRenderer.getInnerHtml(section, {
      id: 'shopify-section-wishlist-updated-modal',
      selector: '.shopify-section',
    })

    if (!html) return

    modalWrapper.innerHTML = html

    const textEl = modalWrapper.querySelector(`[data-action="${action}"]`)
    textEl?.setAttribute('aria-hidden', 'false')

    const closeIcon = modalWrapper.querySelector('.js-wishlist-updated-close')
    closeIcon.addEventListener('click', () => modalWrapper.dataset.active = 'false')

    setTimeout(() => {
      modalWrapper.dataset.active = 'true'
    }, 150);
  }

  addToWishlist() {
    // add product from wishlist
    // push event to klaviyo

    var item = {
      "ProductName": this.product.title,
      "ProductID": this.product.id,
      "ImageURL": this.product.image_url,
      "URL": this.product.url,
      "Brand": "Triangl",
      "Price": this.product.price,
      "CompareAtPrice": this.product.compareAtPrice,
      "Currency": this.product.currency
    };
    klaviyo.push(["track", "Wishlist Add", item]);

    // update the button state, changes the look of the button to filled heart
    this.dataset.action = 'remove'

    let dataLayerData = this.getAnalyticsData()

    return new Promise(async (resolve, reject) => {
      try {

        if (window.customer?.id) {
          let obj = {
            'customer': window.customer.id.toString(),
            'action': 'add',
            'tag': `wishlist:${this.product.id}`
          }

          await this.makeApiCall(this.apiUrl, obj)
        }

        // fetch product information from the API
        this.product.getProductInformation(this.product.id).then((data) => {

          // check if data is empty
          if (Object.keys(data).length === 0) return

          this.product.gid = data.id
          this.product.image_url = data.images.edges[0].node.originalSrc
          this.product.url = this.product.generateProductURL(data.handle)
          this.product.handle = data.handle
          this.product.title = data.title
          this.product.added_to_wishlist = this.getFormattedDateTime(new Date())
          this.product.price = `${currencySymbol}${parseFloat(data.priceRange.minVariantPrice.amount).toFixed(2)}`

          this.updateLocalStorageItem('add', this.product)

          dataLayerData.action = 'add'
          document.dispatchEvent(new CustomEvent('wishlist-item-added', { bubbles: true, detail: dataLayerData }))
        })
        resolve();
      } catch (error) {
        // revert button state
        this.dataset.action = 'add'

        reject(error)
      }
    })
  }

  removeFromWishlist() {
    // remove product from wishlist
    // push event to klaviyo

    var item = {
      "ProductName": this.product.title,
      "ProductID": this.product.id,
      "ImageURL": this.product.image_url,
      "URL": this.product.url,
      "Brand": "Triangl",
      "Price": this.product.price,
      "CompareAtPrice": this.product.compareAtPrice,
      "Currency": this.product.currency
    };

    klaviyo.push(["track", "Wishlist Remove", item]);

    // update button state
    this.dataset.action = 'add'

    let dataLayerData = this.getAnalyticsData()

    return new Promise(async (resolve, reject) => {
      try {

        if (window.customer?.id) {
          // remove the product from customer's tags
          let obj = {
              'customer': window.customer.id.toString(),
              'action': 'remove',
              'tag': `wishlist:${this.product.id}`
          }
          await this.makeApiCall(this.apiUrl, obj)
        }

        // update local storage
        this.updateLocalStorageItem('remove', this.product)

        // remove product card from wishlist page
        this.removeProductCardFromDOM()

        // update dataLayer
        dataLayerData.action = 'remove'
        document.dispatchEvent(new CustomEvent('wishlist-item-removed', { bubbles: true, detail: dataLayerData }))

        resolve()
      } catch (error) {
        // revert the button state
        this.dataset.action = 'remove'
        reject(error)
      }
    })
  }
}

class FostrWishlistItems extends FostrWishlist {
  // Wishlist product grid shown on the wishlist page
  constructor() {
    super()
    this.wishlistPageSetState()
    this.renderWishlistPageItems()
  }

  renderWishlistPageItems() {
    let localStorageItem = this.getLocalStorageItem()

    const ulEl = this.querySelector('ul')
    if (!ulEl) return

    // for each item in wishlist, create a product card
    localStorageItem.products.forEach((product, index) => {
      const alreadyExists = this.querySelector(`[data-product-id="${product.id}"]`)
      if (alreadyExists) return

      const productCard = document.createElement('fostr-wishlist-product-card')
      productCard.dataset.productId = product.id

      const li = document.createElement('li')
      li.classList.add('grid__item')
      li.appendChild(productCard)
      ulEl.appendChild(li)
    })
  }
}

class FostrWishlistProductCard extends FostrWishlist {
  async getProductCardHTML(productId) {

    let localStorageItem = this.getLocalStorageItem()

    // get product from localStorageItem.products by id
    let product = localStorageItem.products.find(product => product.id == productId)

    if (!product) {
      this.generateProductsFromCustomerTags(window.customer.tags).then(() => {
        product = localStorageItem.products.find(product => product.id == productId)
      })

      // remove product card from wishlist page
      this.parentNode.remove()
      return
    }

    try {
      const response = await fetch(`${Shopify.routes.root}products/${product.handle}?view=product-card-wishlist`)
      if (!response.ok) {
        throw new Error(`Fetch request failed with status ${response.status}`)
      }

      const responseText = await response.text()
      return this.parseHTML(responseText) // productHTML
    } catch (err) {
      console.error(err)
    }
  }

  constructor() {
    super()
  }

  connectedCallback() {

    const localStorageItem = this.getLocalStorageItem()

    // get the product id from the data-product-id attribute
    this.getProductCardHTML(this.dataset.productId).then((html) => {
      if (!html) return
      this.outerHTML = html.outerHTML
    })
  }

  parseHTML = (htmlString) => {
    const html = new DOMParser().parseFromString(htmlString, 'text/html')
    return html.querySelector('product-card')
  }
}

class FostrWishlistHiddenField extends HTMLElement {

  productId = null

  constructor() {
    super()
  }

  connectedCallback() {

    this.updateContent()

    // attribute observer for data-variant-id
    const config = { attributes: true }
    const observer = new MutationObserver(this.updateContent.bind(this))
    observer.observe(this, config)

    // observe custom events
    document.addEventListener('wishlist-item-added', this.updateContent.bind(this))
    document.addEventListener('wishlist-item-removed', this.updateContent.bind(this))
  }

  disconnectedCallback() {
    // remove observer
    const observer = new MutationObserver(this.updateContent.bind(this))
    observer.disconnect()

    // remove event listeners
    document.removeEventListener('wishlist-item-added', this.updateContent.bind(this))
    document.removeEventListener('wishlist-item-removed', this.updateContent.bind(this))
  }

  updateContent() {
    this.setProductId()

    if (!this.productId || !this.isWishlistItem()) {
      // remove hidden field
      this.innerHTML = ''
      return
    }
    this.createHiddenField()
  }

  setProductId() {
    this.productId = this.dataset.productId
  }

  createHiddenField() {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'properties[_is_wishlist_item]'
    input.value = 'true'
    this.innerHTML = ''
    this.appendChild(input)
  }

  // check if the product is in the wishlist
  isWishlistItem() {
    // get fostrWishlist from localStorage
    const localStorageItem = JSON.parse(localStorage.getItem('fostrWishlist'))
    if (!localStorageItem) return false

    // check if the product is in the wishlist
    return localStorageItem.products.find(product => product.id == this.productId)
  }
}

class FostrWishlistModal extends FostrWishlist {
  constructor() {
    super();
    let totalItems = this.getLocalStorageItem().productCount;
    if (totalItems > 0) {
      /* don't open login prompt if user has dismissed it */
      if (localStorage.getItem('wishlist-login-prompt-dismissed') == null) {
        this.classList.add('open');
      } else {
        /* always open login prompt on wishlist page */
        if (window.location.href.includes('wishlist')) {
          this.classList.add('open');
        }
      }
    }
    this.addEventListener('click', (e) => {
      if (e.target == this || e.target.classList.contains('button')) {
        this.classList.remove('open');
        localStorage.setItem('wishlist-login-prompt-dismissed', 'true')
      }
    })
    this.querySelector('svg').addEventListener('click', (e) => {
      this.classList.remove('open');
      localStorage.setItem('wishlist-login-prompt-dismissed', 'true')
    })
  }
}

if (!customElements.get('fostr-wishlist')) {
  customElements.define('fostr-wishlist', FostrWishlist);
}

if (!customElements.get('fostr-wishlist-modal')) {
  customElements.define('fostr-wishlist-modal', FostrWishlistModal);
}

if (!customElements.get('fostr-wishlist-button')) {
  customElements.define('fostr-wishlist-button', FostrWishlistButton);
}

if (!customElements.get('fostr-wishlist-items')) {
  customElements.define('fostr-wishlist-items', FostrWishlistItems);
}

if (!customElements.get('fostr-wishlist-product-card')) {
  customElements.define('fostr-wishlist-product-card', FostrWishlistProductCard);
}

if (!customElements.get('fostr-wishlist-hidden-field')) {
  customElements.define('fostr-wishlist-hidden-field', FostrWishlistHiddenField);
}
